import React from 'react';
import { ChevronLeft, ChevronRight, Plus, Settings, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const MobileBottomNav = ({
  currentDate,
  onMonthChange,
  onAddTransaction,
  onSettingsClick,
  onFiltersClick,
  onPlannedClick,
  onToggleCharts,
  plannedCount,
  filtersActive,
  balance,
  plannedCostBalance,
  showCharts,
  dateMode,
}) => {
  const isMonthNavActive = dateMode === 'month';
  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-neutral-100 z-50 safe-area-bottom shadow-[0_-4px_24px_rgba(0,0,0,0.06)]">
      {/* Balance Strip (global, always visible) */}
      <div className="relative grid grid-cols-[1fr_auto_1fr] items-stretch gap-1 px-2 pt-3 pb-1">
        <button
          onClick={onToggleCharts}
          className={`flex flex-col items-start px-2.5 py-1 rounded-xl transition-all active:scale-[0.97] min-w-0 ${
            showCharts ? 'bg-primary-50/80' : 'hover:bg-neutral-100'
          }`}
          aria-label="Ausgabenübersicht anzeigen"
          aria-pressed={showCharts}
        >
          <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider truncate">Kontostand</span>
          <span className={`text-xl font-bold leading-tight tabular-nums truncate ${balance >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {balance?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) || '0,00 €'}
          </span>
        </button>

        {/* Spacer reserving room for the floating Add FAB */}
        <div className="w-14 shrink-0" />

        <button
          onClick={onPlannedClick}
          className="relative flex flex-col items-end px-2.5 py-1 rounded-xl transition-all hover:bg-neutral-100 active:scale-[0.97] min-w-0"
          aria-label="Geplante Einträge anzeigen"
        >
          <span className="flex items-center gap-1 max-w-full">
            {plannedCount > 0 && (
              <span className="shrink-0 w-4 h-3.5 bg-primary-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                {plannedCount}
              </span>
            )}
            <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider truncate">Prognose</span>
          </span>
          <span className={`text-xl font-bold leading-tight tabular-nums truncate ${plannedCostBalance >= 0 ? 'text-primary-600' : 'text-red-500'}`}>
            {plannedCostBalance?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) || '0,00 €'}
          </span>
        </button>

        {/* Floating centered Add Transaction FAB */}
        <button
          onClick={onAddTransaction}
          className="absolute left-1/2 -top-2 -translate-x-1/2 w-12 h-12 flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700 text-white rounded-full shadow-lg ring-4 ring-white hover:shadow-xl hover:from-primary-600 hover:to-primary-800 transition-all active:scale-95"
          aria-label="Neue Transaktion"
        >
          <Plus size={20} strokeWidth={2.5} />
        </button>
      </div>

      {/* Navigation Row */}
      <div className="flex items-center justify-center gap-2 px-2 py-2">
        {/* Settings */}
        <button
          onClick={onSettingsClick}
          className="p-3 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-xl transition-all"
          aria-label="Einstellungen"
        >
          <Settings size={21} />
        </button>

        {/* Month Navigation (center) - only meaningful in month mode */}
        {isMonthNavActive ? (
          <div className="flex items-stretch gap-0.5 bg-neutral-100 rounded-xl px-1 flex-1 max-w-[220px] transition-opacity">
            <button
              onClick={() => onMonthChange(-1)}
              className="flex-1 flex items-center justify-center py-2 text-neutral-600 hover:text-neutral-900 rounded-lg transition-all active:scale-90 disabled:pointer-events-none"
              aria-label="Vorheriger Monat"
            >
              <ChevronLeft size={22} strokeWidth={2.5} />
            </button>
            <div className="py-1 px-1 min-w-[86px] flex flex-col items-center justify-center text-center select-none">
              <div className="text-sm font-bold text-neutral-800">
                {format(currentDate, 'MMMM', { locale: de })}
              </div>
              <div className="text-xs text-neutral-500">
                {format(currentDate, 'yyyy', { locale: de })}
              </div>
            </div>
            <button
              onClick={() => onMonthChange(1)}
              className="flex-1 flex items-center justify-center py-2 text-neutral-600 hover:text-neutral-900 rounded-lg transition-all active:scale-90 disabled:pointer-events-none"
              aria-label="Nächster Monat"
            >
              <ChevronRight size={22} strokeWidth={2.5} />
            </button>
          </div>
        ) : (
          <div className="flex-1" />
        )}

        {/* Filter */}
        <button
          onClick={onFiltersClick}
          className={`relative p-3 rounded-xl transition-all ${
            filtersActive ? 'text-primary-600 bg-primary-50' : 'text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100'
          }`}
          aria-label="Filter"
        >
          <Filter size={21} />
          {filtersActive && (
            <span className="absolute top-2 right-2 w-1.5 h-1.5 bg-primary-500 rounded-full" />
          )}
        </button>
      </div>
    </div>
  );
};

export default MobileBottomNav;
